import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { ChevronDown, X } from 'lucide-react';

/**
 * Small toggle + customer-picker used inside the Create Invoice, Convert Memo,
 * and Edit Dispatch dialogs to bill an invoice to a *different* customer than
 * the order's owner (e.g. remaining pieces sold to another company).
 *
 * The toggle drives a local "enabled" flag independent of whether a customer
 * has been picked yet — so the picker area opens as soon as the user flips it
 * on. If the toggle is switched off without picking a customer, no bill-to is
 * applied (upstream `value.id` stays empty).
 */
const BillToPicker = ({
  open, setOpen,
  approvedCustomers, loading,
  orderCustomerName, orderCustomerId,
  value,      // { id: string, name: string }
  onChange,   // (customerObject | null) => void
  testIdPrefix = 'billto',
}) => {
  // Local toggle state. Auto-enables when a customer is already selected
  // (e.g. Edit Dispatch dialog opening on an invoice that already has bill-to).
  const [enabled, setEnabled] = useState(!!value?.id);

  // Sync the toggle whenever a customer is programmatically set/cleared from
  // outside (e.g. dialog re-opens on a different dispatch).
  useEffect(() => {
    if (value?.id) setEnabled(true);
  }, [value?.id]);

  const handleToggle = (v) => {
    setEnabled(v);
    // If the user turns the switch OFF, clear any picked customer.
    if (!v && value?.id) onChange(null);
  };

  return (
    <div className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Bill to a different customer</Label>
          <p className="text-[11px] text-muted-foreground leading-tight">
            Invoice will land under the selected company&apos;s Receivables & Ledger.
            {orderCustomerName ? ` Order stays with ${orderCustomerName}.` : ''}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          data-testid={`${testIdPrefix}-toggle`}
        />
      </div>
      {enabled && (
        <div className="pt-1">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                className="w-full justify-between h-9 font-normal"
                data-testid={`${testIdPrefix}-combobox`}
              >
                <span className="truncate text-sm">
                  {value?.name || (loading ? 'Loading customers…' : 'Select a customer…')}
                </span>
                <div className="flex items-center gap-1">
                  {value?.id && (
                    <X
                      className="h-3.5 w-3.5 opacity-60 hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); onChange(null); }}
                    />
                  )}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search customers..." className="h-9" />
                <CommandList>
                  <CommandEmpty>
                    {approvedCustomers.length === 0
                      ? 'No approved customers yet.'
                      : 'No customers match your search.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {approvedCustomers
                      // Hide the order's own customer from the picker — that's the default anyway.
                      .filter(c => c.id !== orderCustomerId)
                      .map(c => (
                        <CommandItem
                          key={c.id}
                          value={`${c.company_name} ${c.contact_person_name || ''} ${c.mobile || ''}`}
                          onSelect={() => { onChange(c); setOpen(false); }}
                          data-testid={`${testIdPrefix}-option-${c.id}`}
                        >
                          <div className="flex flex-col gap-0.5 w-full">
                            <span className="font-medium">{c.company_name}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {c.contact_person_name || '—'} · {c.mobile || 'no mobile'}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {value?.id && (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">
              ✓ Invoice will be billed to <strong>{value.name}</strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BillToPicker;
